import React, { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Formik, Form, useFormikContext } from "formik";
import { Grid, Paper } from "@mui/material";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import { getSmartkioskInitialValues } from "app/pages/customers/dsok/utils/getSmartkioskInitialValues";
import { getDevicesFieldSchema } from "app/pages/customers/dsok/fields/DevicesField";
import { getLocationsFieldSchema } from "app/pages/customers/dsok/smartkioskFields/SmartkioskLocationFields";
import merge from "lodash/merge";
import { mergeDeep } from "app/utils/objectHelpers";
import { DSOK_CUSTOMER_FIELD_SCHEMA } from "app/pages/customers/dsok/smartkioskFields/SmartkioskCustomerFields";
import DevicesField from "app/pages/customers/dsok/fields/DevicesField";
import DsokCustomerDetailLayout from "../components/DsokCustomerDetailLayout";
import DsokCustomerDetailDirtySync from "../components/DsokCustomerDetailDirtySync";
import DsokCustomerDetailSectionRegister from "../components/DsokCustomerDetailSectionRegister";
import { useDsokCustomerDetail } from "../data/useDsokCustomerDetail";

function DevicesSectionRegister() {
  const { values } = useFormikContext();
  const getPayload = useCallback(
    (vals) => {
      const v = vals ?? values;
      const devices = v?.customer?.devices ?? {};
      // Zdroj je POVINNÝ pre zariadenie typu "external" – bez neho blokujeme uloženie.
      for (const key of Object.keys(devices)) {
        const d = devices[key];
        if (String(d?.deviceType).toLowerCase() === "external") {
          const src = d?.externalOrders?.source;
          if (!src || String(src).trim() === "") {
            return null;
          }
        }
      }
      return devices;
    },
    [values],
  );
  return (
    <DsokCustomerDetailSectionRegister
      sectionId="devices"
      getPayload={getPayload}
    />
  );
}

const DEVICES_FIELD_NAME = "DEVICES_FIELD";

/** User typ: 1 = Admin, 2 = Business Partner, 3 = Customer, 7 = Service Partner. */
const TYP_ADMIN = 1;
const TYP_PARTNER = 2;
const TYP_CUSTOMER_USER = 3;
const TYP_SERVICE_PARTNER = 7;

/**
 * Device list for the opened customer: no role-based filtering is applied.
 * Business Partner (typ 2) and Service Partner (typ 7) see the same full list
 * as Admin (typ 1). Data comes from customer.dsokSettings.customer.devices
 * (same API response for all roles). Backend must return full device set for
 * partners for this to match admin.
 */
const DsokCustomerDevicesPage = () => {
  const { customerUrl } = useParams();
  const { t } = useTranslation();
  const auth = useJumboAuth();
  const authUser = auth?.getAuthUser?.() || {};
  const userTyp = authUser?.type ?? authUser?.typ ?? TYP_ADMIN;
  const customerType = authUser?.customer?.customerType ?? authUser?.customer?.customer_type;
  const effectiveTyp = customerType === TYP_SERVICE_PARTNER ? TYP_SERVICE_PARTNER : userTyp;
  const isAdmin = effectiveTyp === TYP_ADMIN;
  const isPartner = effectiveTyp === TYP_PARTNER || effectiveTyp === TYP_SERVICE_PARTNER;
  const isCustomer = effectiveTyp === TYP_CUSTOMER_USER;

  const { customer, loading: customerDetailLoading } = useDsokCustomerDetail();
  /** `undefined` = omit prop (legacy workspace sources). `null` | string = Devices page uses POS Workspaces API only. */
  const posWorkspacesCustomerId = useMemo(() => {
    if (customerDetailLoading) return null;
    if (!customer || typeof customer !== "object") return null;
    const id = customer._id ?? customer.id ?? customer.customerId;
    if (id == null || String(id).trim() === "") return null;
    return String(id).trim();
  }, [customer, customerDetailLoading]);
  const posWorkspacesBeDomain = useMemo(() => {
    if (!customer || typeof customer !== "object") return "";
    const be =
      customer?.dsokSettings?.customer?.beDomain ??
      customer?.beDomain ??
      "";
    return typeof be === "string" ? be.trim() : "";
  }, [customer]);
  const [expanded, setExpanded] = useState(DEVICES_FIELD_NAME);
  const handleChange = (panel) => (_event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const initialCustomerValues = useMemo(() => {
    if (!customer) {
      return {
        ...DSOK_CUSTOMER_FIELD_SCHEMA,
        locations: {},
        devices: {},
      };
    }
    const fullInitial = getSmartkioskInitialValues(customer);
    if (!fullInitial?.customer) {
      const dsokSettings = customer.dsokSettings || {};
      return {
        ...mergeDeep(DSOK_CUSTOMER_FIELD_SCHEMA, dsokSettings.customer || {}),
        locations: merge(
          getLocationsFieldSchema(dsokSettings.customer?.locations),
          dsokSettings.customer?.locations || {}
        ),
        devices: merge(
          getDevicesFieldSchema(dsokSettings.customer?.devices),
          dsokSettings.customer?.devices || {}
        ),
      };
    }
    return fullInitial.customer;
  }, [customer]);

  const formInitialValues = { customer: initialCustomerValues };

  return (
    <DsokCustomerDetailLayout
      lastBreadcrumbLabel={t("dsokCustomerDetail.sections.devices.title")}
    >
      <Paper
        variant="outlined"
        sx={{
          bgcolor: "background.paper",
          p: 2,
          borderRadius: 1,
        }}
      >
        <Formik
          initialValues={formInitialValues}
          enableReinitialize
          onSubmit={() => {}}
        >
          <Form>
            <DsokCustomerDetailDirtySync />
            <DevicesSectionRegister />
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <DevicesField
                  handleChange={handleChange}
                  expanded={expanded}
                  isAdmin={isAdmin}
                  isPartnerAdmin={false}
                  token={undefined}
                  customerUrl={customerUrl ?? ""}
                  isPartner={isPartner}
                  isCustomer={isCustomer}
                  hideSectionTitle
                  posWorkspacesCustomerId={posWorkspacesCustomerId}
                  posWorkspacesBeDomain={posWorkspacesBeDomain}
                />
              </Grid>
            </Grid>
          </Form>
        </Formik>
      </Paper>
    </DsokCustomerDetailLayout>
  );
};

export default DsokCustomerDevicesPage;
